
import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { withTimeout } from '@/utils/timeout';
import { trackEvent } from '@/utils/analytics';

export const useAuthHandlers = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { signIn, signUp, resetPassword } = useAuth();

  const handleLogin = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Add timeout protection to login operation
      const { error } = await withTimeout(
        signIn(email, password),
        15000, // 15 second maximum timeout for login
        'Login operation timed out - please try again'
      );
      
      if (error) {
        setError(error.message);
      } else {
        // Track successful login event
        await trackEvent('login', { method: 'password' });
      }
    } catch (unexpectedError) {
      if (unexpectedError instanceof Error && unexpectedError.message.includes('timed out')) {
        setError('Login is taking longer than expected. Please try again.');
      } else {
        setError('An unexpected error occurred during login');
      }
    } finally {
      // Always reset loading state after timeout or completion
      setIsLoading(false);
    }
  }, [signIn]);

  const handleSignUp = useCallback(async (email: string, password: string, firstName: string, lastName: string, organizationName?: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const { authRegistrationService } = await import('@/services/authRegistrationService');
      const result = await authRegistrationService.registerNewUser(
        email,
        password,
        firstName,
        lastName,
        organizationName
      );

      if (result.success) {
        setSuccess('Account created successfully! Please check your email to verify your account.');
      } else {
        setError(result.error || 'Failed to create account');
      }
    } catch (unexpectedError) {
      setError('An unexpected error occurred during signup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleResetPassword = useCallback(async (email: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!email) {
        setError('Please enter your email address');
        return;
      }

      const { error } = await withTimeout(
        resetPassword(email),
        10000, // 10 second timeout for password reset
        'Password reset request timed out - please try again'
      );
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess('Password reset email sent! Check your inbox.');
      }
    } catch (unexpectedError) {
      if (unexpectedError instanceof Error && unexpectedError.message.includes('timed out')) {
        setError('Password reset request is taking longer than expected. Please try again.');
      } else {
        setError('An unexpected error occurred during password reset');
      }
    } finally {
      setIsLoading(false);
    }
  }, [resetPassword]);

  const handleResendConfirmation = useCallback(async (email: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!email) {
        setError('Please enter your email address');
        return;
      }

      const { authService } = await import('@/contexts/auth/authService');
      const { error } = await withTimeout(
        authService.resendConfirmationEmail(email),
        10000,
        'Resend request timed out - please try again'
      );
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess('Confirmation email sent! Please check your inbox.');
      }
    } catch (unexpectedError) {
      if (unexpectedError instanceof Error && unexpectedError.message.includes('timed out')) {
        setError('Resend request is taking longer than expected. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  return {
    isLoading,
    error,
    success,
    handleLogin,
    handleSignUp,
    handleResetPassword,
    handleResendConfirmation,
    clearMessages
  };
};
